import type { ReebeloStoreT } from '@lambda/reebelo';
import settings from '@/settings';

/**
 * Trustpilot's locale sites for the markets that have one.
 *
 * Reviews live on one business unit, but each locale site presents them in that
 * market's language — which is the point: a French shopper sent to
 * www.trustpilot.com lands on untranslated reviews.
 *
 * Markets are absent here when Trustpilot has no site for them: `sg.` redirects
 * straight back to `www.`, so Singapore is deliberately not listed. Anything
 * unlisted falls back to the international site.
 */
const TRUSTPILOT_SUBDOMAIN: Partial<Record<ReebeloStoreT, string>> = {
  'reebelo-fr': 'fr',
  'reebelo-de': 'de',
  'reebelo-at': 'de',
  'reebelo-be': 'fr',
  'reebelo-pt': 'pt',
  'reebelo-fi': 'fi',
  'reebelo-es': 'es',
  'reebelo-it': 'it',
  'reebelo-se': 'se',
  'reebelo-uk': 'uk',
  'reebelo-ca': 'ca',
  'reebelo-au': 'au',
  'reebelo-nz': 'nz',
};

/** The business unit is the same everywhere; only the locale site changes. */
const TRUSTPILOT_BUSINESS_UNIT = 'reebelo.com';

export const getTrustpilotReviewUrl = (
  store: ReebeloStoreT = settings.store,
): string =>
  `https://${
    TRUSTPILOT_SUBDOMAIN[store] ?? 'www'
  }.trustpilot.com/review/${TRUSTPILOT_BUSINESS_UNIT}`;

export const TRUSTPILOT_REVIEW_URL = getTrustpilotReviewUrl();

/**
 * Locales Trustpilot's embedded widget accepts, per market.
 *
 * Listed explicitly rather than derived from the store locale because
 * Trustpilot supports a fixed set — `en-SG` is not one of them, so Singapore
 * falls back with everything else rather than silently breaking the widget.
 */
const TRUSTPILOT_WIDGET_LOCALES: Partial<Record<ReebeloStoreT, string>> = {
  'reebelo-us': 'en-US',
  'reebelo-uk': 'en-GB',
  'reebelo-au': 'en-AU',
  'reebelo-nz': 'en-NZ',
  'reebelo-ca': 'en-CA',
  'reebelo-fr': 'fr-FR',
  'reebelo-de': 'de-DE',
  'reebelo-at': 'de-DE',
  'reebelo-be': 'fr-BE',
  'reebelo-pt': 'pt-PT',
  'reebelo-fi': 'fi-FI',
  'reebelo-es': 'es-ES',
  'reebelo-it': 'it-IT',
  'reebelo-se': 'sv-SE',
};

const DEFAULT_WIDGET_LOCALE = 'en-US';

/** `data-locale` for the embedded review widget. */
export const TRUSTPILOT_WIDGET_LOCALE =
  TRUSTPILOT_WIDGET_LOCALES[settings.store] ?? DEFAULT_WIDGET_LOCALE;

/**
 * `data-review-languages` — which reviews the widget shows. Matches the widget
 * locale so a shopper is not shown reviews in a language they did not ask for.
 */
export const TRUSTPILOT_REVIEW_LANGUAGE =
  TRUSTPILOT_WIDGET_LOCALE.split('-')[0] ?? 'en';
